import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Injector, Input } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { IInputComponent } from '@nimbus-library/features/shared/components/material/i-input-component';

@Component({
  standalone: false,
  selector: 'nimbus-input-time-picker-by-duration',
  templateUrl: './input-time-picker-by-duration.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputTimePickerByDurationComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputTimePickerByDurationComponent),
      multi: true,
    },
  ],
})
export class InputTimePickerByDurationComponent extends IInputComponent<number> implements Validator {
  @Input()
  public format: string = 'HH:mm:ss';
  @Input()
  public formatErrors: boolean = false;
  @Input()
  public removable: boolean = false;

  private innerMin: number;
  private innerMax: number;

  @Input()
  public set min(value: number) {
    this.innerMin = value;
  }

  @Input()
  public set max(value: number) {
    this.innerMax = value;
  }

  public get min(): number {
    return this.innerMin;
  }

  public get max(): number {
    return this.innerMax;
  }

  public constructor(
    private changeDetectionRef: ChangeDetectorRef,
    private injector: Injector,
    private readonly translateService: TranslateService,
  ) {
    super(changeDetectionRef, injector, translateService);
  }

  public writeValue(value: number): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
    }
    this.changeDetectionRef.markForCheck();
  }

  public validate(control: AbstractControl): ValidationErrors {
    let result: ValidationErrors = null;
    if (control && control.value) {
      if (this.max && control.value > this.max) {
        result = { max: { max: this.formatError(this.max) } };
      } else if (this.min && control.value < this.min) {
        result = { min: { min: this.formatError(this.min) } };
      }
    } else {
      result = null;
    }
    return result;
  }

  private formatError(value: number): string {
    if (this.formatErrors === false) {
      return value.toString();
    }

    const duration = moment.duration({ seconds: value });
    return this.format
      .toLowerCase()
      .replace('dd', Math.floor(duration.asDays()).toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false }))
      .replace('hh', duration.hours().toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false }))
      .replace('mm', duration.minutes().toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false }))
      .replace('ss', duration.seconds().toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false }));
  }
}
